/**
 * The colours a jaw scan is drawn in when it carries no colour of its own.
 *
 * The same whole-arch tint as the 3D PDF (dental3dpdf's PREP_COLOUR), so the
 * dentist sees one look in the browser and in Acrobat. The opposing jaw takes
 * a cooler mauve of the same depth, so the two arches read apart at a glance
 * without either looking unnatural. Scans with the scanner's own colour keep it.
 */
import type { LayerKind } from '@/types';

/** The jaw the crown sits in - the 3D PDF's arch colour. */
export const CROWN_JAW_COLOUR = '#d99a9e';
/** The opposing jaw. */
export const OPPOSING_JAW_COLOUR = '#c4a7bd';

export function isUpperTooth(tooth: string): boolean {
  return tooth.startsWith('1') || tooth.startsWith('2');
}

/** Which arch the crowns are in, from their FDI numbers; null when none says. */
export function crownArch(teeth: readonly (string | undefined)[]): 'upper' | 'lower' | null {
  const tooth = teeth.find((t): t is string => Boolean(t));
  if (!tooth) return null;
  return isUpperTooth(tooth) ? 'upper' : 'lower';
}

/**
 * A jaw's colour, or null for a layer that is not a jaw. A scan from before
 * treatment is of the crown's own arch. With no crown tooth to go by, both
 * jaws take the crown jaw's colour, as the 3D PDF does.
 */
export function jawColour(kind: LayerKind, arch: 'upper' | 'lower' | null): string | null {
  if (kind === 'preop') return CROWN_JAW_COLOUR;
  if (kind !== 'upper' && kind !== 'lower') return null;
  if (arch === null || kind === arch) return CROWN_JAW_COLOUR;
  return OPPOSING_JAW_COLOUR;
}
